import React,
{
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ReplyOutlined } from '@material-ui/icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import useStyles from './styles';
import Snackbar from '../../../components/Snackbar';
import { deleteTaskInstance, getTaskInstanceById, putEditTaskInstanceById } from '../../../requests/api/taskInstance';
import { getUsersPerPageByCompanyId } from '../../../requests/api/user';
import { getLocationsPerPageByCompanyId } from '../../../requests/api/location';
import SplashScreen from '../../../components/Loading/SplashScreen';
import { getCategoriesPerPage } from '../../../requests/api/category';
import { getProductGroupsPerPage } from '../../../requests/api/product-groups';
import sleep from '../../../utils/sleep';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const nullObj = { id: null, name: '-' };

const TaskInstanceEdit = () => {
  const { taskId, taskInstanceId } = useParams();
  const [taskInstance, setTaskInstance] = useState(null);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');
  const taskDetailUrl = `/tasks/${taskId}`;
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const title = t('taskInstanceEdit.title');

  const [userSearch, setUserSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [productGroupSearch, setProductGroupSearch] = useState('');

  const [loading, setLoading] = useState(true);

  const debouncedUserSearch = useDebounce(userSearch, 400);
  const debouncedLocationSearch = useDebounce(locationSearch, 400);
  const debouncedCategorySearch = useDebounce(categorySearch, 400);
  const debouncedProductGroupSearch = useDebounce(productGroupSearch, 400);

  const [showUsers, setShowUsers] = useState(false);
  const [showLocations, setShowLocations] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showProductGroups, setShowProductGroups] = useState(false);

  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productGroups, setProductGroups] = useState([]);

  const wrapperRefUser = useRef(null);
  const wrapperRefLocation = useRef(null);
  const wrapperRefCategory = useRef(null);
  const wrapperRefProductGroup = useRef(null);

  const [openDialog, setOpenDialog] = useState(false);
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  useEffect(() => {
    function handlePointerDown(e) {
      if (wrapperRefUser.current && !wrapperRefUser.current.contains(e.target)) {
        setShowUsers(false);
      }
      if (wrapperRefLocation.current && !wrapperRefLocation.current.contains(e.target)) {
        setShowLocations(false);
      }
      if (wrapperRefCategory.current && !wrapperRefCategory.current.contains(e.target)) {
        setShowCategories(false);
      }
      if (wrapperRefProductGroup.current && !wrapperRefProductGroup.current.contains(e.target)) {
        setShowProductGroups(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, []);

  const resetTimes = useMemo(() => [
    { name: t('task.association.resetTimes.placeholder'), value: '' },
    { name: t('task.association.resetTimes.hourly'), value: 'hourly' },
    { name: t('task.association.resetTimes.daily'), value: 'daily' },
    { name: t('task.association.resetTimes.weekly'), value: 'weekly' },
    { name: t('task.association.resetTimes.biweekly'), value: 'biWeekly' },
    { name: t('task.association.resetTimes.monthly'), value: 'monthly' },
    { name: t('task.association.resetTimes.quarterly'), value: 'quartely' },
    { name: t('task.association.resetTimes.annual'), value: 'annual' },
    { name: t('task.association.resetTimes.notClose'), value: 'notClose' },
  ], [t]);

  const firstLoad = (ti) => {
    setLocations([{ ...nullObj }]);
    setLocationSearch('-');
    setCategories([{ ...nullObj }]);
    setCategorySearch('-');
    setProductGroups([{ ...nullObj }]);
    setProductGroupSearch('-');
    const user = ti.User;
    const location = ti.Location;
    const category = ti.Category;
    const productGroup = ti.ProductGroup;
    setUsers([user]);
    setUserSearch(user.email);
    if (location) {
      setLocations([{ ...nullObj }, location]);
      setLocationSearch(location.name);
    }
    if (category) {
      setCategories([{ ...nullObj }, category]);
      setCategorySearch(category.name);
    }
    if (productGroup) {
      setProductGroups([{ ...nullObj }, productGroup]);
      setProductGroupSearch(productGroup.name);
    }
    setLoading(false);
  };

  const fetchTaskInstance = useCallback(async () => {
    try {
      const { data, success } = await getTaskInstanceById(taskInstanceId);
      if (success) {
        const auxTaskInstance = data.data?.taskInstance;
        if (auxTaskInstance) {
          setTaskInstance(auxTaskInstance);
          firstLoad(auxTaskInstance);
        }
      }
    } catch (error) {
      setSeverity('error');
      setMessage(t('common.failedLoad'));
    }
  }, [t, taskInstanceId]);

  useEffect(() => {
    fetchTaskInstance();
  }, [fetchTaskInstance]);

  const fetchUsers = async (term) => {
    if (!term) return;
    const response = await getUsersPerPageByCompanyId(0, 20, term, 'email', [], [], true);
    if (!response.success) return setUsers([]);
    setUsers(response.data.data.users);
  };

  const fetchLocations = async (term) => {
    if (!term) return;
    const response = await getLocationsPerPageByCompanyId(0, 20, term, 'name', [], [], true);
    if (!response.success) return setLocations([{ ...nullObj }]);
    const list = [{ ...nullObj }].concat(response.data.data.locations);
    setLocations(list);
  };

  const fetchCategories = async (term) => {
    if (!term) return setCategories([]);
    const response = await getCategoriesPerPage(0, 20, term, 'name', [], [], true);
    if (!response.success) return setCategories([{ ...nullObj }]);
    const list = [{ ...nullObj }].concat(response.data.data.categories);
    setCategories(list);
  };

  const fetchProductGroups = async (term) => {
    if (!term) return setProductGroups([]);
    const response = await getProductGroupsPerPage(0, 20, term, 'name');
    if (!response.success) return setProductGroups([{ ...nullObj }]);
    const list = [{ ...nullObj }].concat(response.data.data.productGroups);
    setProductGroups(list);
  };

  const handleSave = async () => {
    const body = {
      userId: taskInstance.userId,
      locationId: taskInstance.locationId,
      categoryCode: taskInstance.categoryCode,
      productGroupId: taskInstance.productGroupId,
      resetScale: taskInstance.resetScale,
      expectedLocationResponses: parseInt(taskInstance.expectedLocationResponses, 10),
    };
    const response = await putEditTaskInstanceById(taskInstance.id, body);
    if (response.success) {
      setSeverity('success');
      setMessage(t('common.savedSuccessfully'));
    } else {
      setSeverity('error');
      setMessage(t('common.savedUnsuccessfully'));
    }
  };

  const handleDelete = async () => {
    const response = await deleteTaskInstance(taskInstance.id);
    if (response.success) {
      setSeverity('success');
      setMessage(t('common.deletedSuccessfully'));
      sleep(1000).then(() => history.push(taskDetailUrl));
    } else {
      setSeverity('error');
      setMessage(t('common.deletedUnsuccessfully'));
    }
  };

  useEffect(() => {
    fetchUsers(debouncedUserSearch);
  }, [debouncedUserSearch]);

  useEffect(() => {
    fetchLocations(debouncedLocationSearch);
  }, [debouncedLocationSearch]);

  useEffect(() => {
    fetchCategories(debouncedCategorySearch);
  }, [debouncedCategorySearch]);

  useEffect(() => {
    fetchProductGroups(debouncedProductGroupSearch);
  }, [debouncedProductGroupSearch]);

  if (loading) {
    return <SplashScreen />;
  }
  return (
    <>
      <div className={styles.content}>
        <div className={styles.headerContainer}>
          <div className={styles.header}>
            <IconButton
              id="backButton"
              aria-label="back"
              className={styles.backButton}
              component={Link}
              to={taskDetailUrl}
            >
              <ReplyOutlined fontSize="small" />
              <span>{t('groups.actionButtons.back')}</span>
            </IconButton>
            <Typography variant="h4" className={styles.title}>
              {title}
            </Typography>
            <Typography >
              <span className={styles.titleTask}>{t('taskInstanceEdit.taskTitle')}</span>  {taskInstance?.Survey?.title || ''}
            </Typography>
            <br />
          </div>
          <div className={styles.buttonsContainer}>
            <Button
              className={styles.deleteButton}
              onClick={() => handleOpenDialog()}
            >
              {t('common.delete')}
            </Button>
          </div>
          <div className={styles.buttonsContainer}>
            <Button
              className={styles.normalButton}
              onClick={() => handleSave()}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
        <Grid container spacing={2}>
          {/* USER SEARCH */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('taskInstanceEdit.form.user')}
              value={userSearch}
              onChange={(e) => {
                setUserSearch(e.target.value);
                setShowUsers(true);
              }}
              onFocus={() => setShowUsers(true)}
            />
            <div className={styles.drowpdownWrapper} ref={wrapperRefUser}>
              {showUsers && users.length > 0 && (
                <div className={styles.searchedDropdown}>
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className={styles.searchedItem}
                      onClick={() => {
                        setUserSearch(user.email);
                        setShowUsers(false);
                        setTaskInstance({ ...taskInstance, userId: user.id });
                      }}
                    >
                      <div className={styles.searchedOptions}>{user.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Grid>
          {/* LOCATION SEARCH */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('taskInstanceEdit.form.location')}
              value={locationSearch}
              onChange={(e) => {
                setLocationSearch(e.target.value);
                setShowLocations(true);
              }}
              onFocus={() => setShowLocations(true)}
            />
            <div className={styles.drowpdownWrapper} ref={wrapperRefLocation}>
              {showLocations && locations.length > 0 && (
                <div className={styles.searchedDropdown}>
                  {locations.map((loc) => (
                    <div
                      key={loc.id}
                      className={styles.searchedItem}
                      onClick={() => {
                        setLocationSearch(loc.name);
                        setShowLocations(false);
                        setTaskInstance({ ...taskInstance, locationId: loc.id });
                      }}
                    >
                      <div className={styles.searchedOptions}>{loc.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Grid>
          {/* CATEGORY SEARCH */}
          <Grid item xs={12}>
            <div>
              <TextField
                fullWidth
                label={t('taskInstanceEdit.form.category')}
                value={categorySearch}
                onChange={(e) => {
                  setCategorySearch(e.target.value);
                  setShowCategories(true);
                }}
                onFocus={() => setShowCategories(true)}
              />
              <div className={styles.drowpdownWrapper} ref={wrapperRefCategory}>
                {showCategories && categories.length > 0 && (
                  <div className={styles.searchedDropdown}>
                    {categories.map((cat) => (
                      <div
                        key={cat.code}
                        className={styles.searchedItem}
                        onClick={() => {
                          setCategorySearch(cat.name);
                          setShowCategories(false);
                          setTaskInstance({
                            ...taskInstance,
                            categoryCode: cat.code,
                          });
                        }}
                      >
                        <div className={styles.searchedOptions}>{cat.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Grid>
          {/* PRODUCT GROUP SEARCH */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('taskInstanceEdit.form.productGroup')}
              value={productGroupSearch}
              onChange={(e) => {
                setProductGroupSearch(e.target.value);
                setShowProductGroups(true);
              }}
              onFocus={() => setShowProductGroups(true)}
            />
            <div className={styles.drowpdownWrapper} ref={wrapperRefProductGroup}>
              {showProductGroups && productGroups.length > 0 && (
                <div className={styles.searchedDropdown}>
                  {productGroups.map((group) => (
                    <div
                      key={group.id}
                      className={styles.searchedItem}
                      onClick={() => {
                        setProductGroupSearch(group.name);
                        setShowProductGroups(false);
                        setTaskInstance({
                          ...taskInstance,
                          productGroupId: group.id,
                        });
                      }}
                    >
                      <div className={styles.searchedOptions}>
                        {group.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Grid>
          {/* SCALE SELECTOR */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="scale-selector-label">
                {t('taskInstanceEdit.form.scale')}
              </InputLabel>
              <Select
                labelId="scale-selector-label"
                id="scale-selector"
                value={taskInstance?.resetScale || ''}
                onChange={(e) => setTaskInstance({ ...taskInstance, resetScale: e.target.value })
                }
              >
                {resetTimes.map((scale) => (
                  < MenuItem key={scale.value} value={scale.value} >
                    {scale.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* EXPECTED LOCATION RESPONSES */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                type="number"
                id="expected-location-responses-selector"
                label={t('task.association.resetTimes.placeholder')}
                value={taskInstance?.expectedLocationResponses || ''}
                onChange={(e) => setTaskInstance({
                  ...taskInstance,
                  expectedLocationResponses: e.target.value,
                })
                }
              />
            </FormControl>
          </Grid>
        </Grid>
      </div>
      <Snackbar
        open={message !== ''}
        message={message}
        severity={severity}
        onClose={() => setMessage('')}
      />
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('taskInstanceEdit.deleteMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} className={styles.normalButton}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} className={styles.deleteButton}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskInstanceEdit;
